/**
 * The tutorial, as data.
 *
 * The beats are the 2020 script's, in its order, because that order was well judged: the
 * pieces, then capturing, then the one asymmetry, then the bow as the exception that
 * explains it, then the things you only need once you are playing. Three beats are new —
 * check, checkmate and the clock — and the bow gets an extra one, where you build the
 * formation yourself rather than being handed it.
 *
 * Two adaptations from 2020, both forced by the rules being properly enforced now. Its
 * teaching positions often had no kings at all, which is not a legal position: a player
 * with no kings has already lost, so the engine correctly offers them no moves. Every
 * setup here therefore has a king a side, parked out of the way when it is not the point.
 * And positions are rebuilt from scratch for each beat rather than accumulating, so a
 * learner who wanders cannot strand themselves.
 *
 * The learner is always player 0.
 */

import type { Step } from './steps.ts';

/** Somewhere for a king to stand while the lesson is about something else. */
const LEARNER_KING_PARKED = '0,5,-5';
const RIVAL_KING_PARKED = '0,-5,5';

const CENTRE_NEIGHBOURS = ['1,-1,0', '1,0,-1', '0,1,-1', '-1,1,0', '-1,0,1', '0,-1,1'];

export const SCRIPT: readonly Step[] = [
  // --- the pieces -------------------------------------------------------

  {
    say:
      "Welcome. You're blue, and everything here is the real game — the same rules the server runs, running in your browser.",
  },
  {
    setup: {
      reset: true,
      spawns: [12, 12],
      cells: { '0,0,0': 'k0', [RIVAL_KING_PARKED]: 'k1' },
    },
  },
  {
    say:
      'This is your king. Every piece moves one cell, in any of the six directions. Move it.',
  },
  {
    await: { type: 'movePiece' },
    hint: CENTRE_NEIGHBOURS,
    nudge:
      'Drag the king to any neighbouring cell — or tap it, then tap where it should go.',
  },
  { wait: 500 },

  {
    say:
      'You also have twelve pawns in reserve. Place one next to your king: pawns can only be put down beside a king of yours.',
  },
  {
    await: { type: 'spawnPiece' },
    nudge: 'Tap the row of pawns in the panel, then tap a cell next to your king.',
  },
  { wait: 500 },

  // --- capturing --------------------------------------------------------

  {
    setup: {
      reset: true,
      spawns: [12, 12],
      cells: { '0,0,0': 'k0', '1,0,-1': 'p1', [RIVAL_KING_PARKED]: 'k1' },
    },
  },
  { say: 'Pieces capture by moving onto an enemy. Take that red pawn with your king.' },
  {
    await: { type: 'movePiece', to: '1,0,-1' },
    hint: ['1,0,-1'],
    nudge: 'Your king is next to it. Move onto it.',
  },
  {
    say:
      'Notice your reserve went up. A king that captures recruits what it took, so you now have thirteen pawns in hand.',
  },
  { wait: 500 },

  // --- the one asymmetry ------------------------------------------------

  {
    setup: {
      reset: true,
      spawns: [12, 12],
      cells: {
        '0,0,0': 'p0',
        '1,0,-1': 'p1',
        '2,0,-2': 'k0',
        [RIVAL_KING_PARKED]: 'k1',
      },
    },
  },
  {
    say:
      'Here is the one asymmetry in the game: a pawn may not capture another pawn. Pick up your pawn and you will see the red pawn is not offered.',
  },
  { say: 'Your king has no such problem. Take the red pawn with the king.' },
  {
    await: { type: 'movePiece', from: '2,0,-2', to: '1,0,-1' },
    hint: ['2,0,-2', '1,0,-1'],
    nudge: 'Use the king — the piece two cells to the right of the red pawn.',
  },
  { wait: 500 },

  // --- the bow ----------------------------------------------------------

  {
    say: 'There is one exception to that rule, and it is what the game is really about.',
  },
  {
    setup: {
      reset: true,
      spawns: [12, 12],
      cells: {
        '0,0,0': 'p0',
        '-1,1,0': 'p0',
        '0,-1,1': 'p0',
        '3,0,-3': 'p1',
        [LEARNER_KING_PARKED]: 'k0',
        [RIVAL_KING_PARKED]: 'k1',
      },
    },
  },
  {
    say:
      'Three of your pieces at 120° form a bow. The piece at the elbow is launched like an arrow: it glides in a straight line until something stops it.',
  },
  {
    say:
      'Pick up the middle pawn. The board will draw the bow — the two pieces lending support, the string pulled back into the empty cell behind, and the shot. Take the red pawn with it.',
  },
  {
    await: { type: 'movePiece', from: '0,0,0', to: '3,0,-3', glide: true },
    hint: ['0,0,0', '3,0,-3'],
    nudge:
      'Pick up the pawn in the middle of the elbow and send it to the red pawn three cells away.',
  },
  {
    say:
      'And there it is: a pawn just captured a pawn. Shot from a bow, anything may be taken.',
  },
  { wait: 500 },

  // --- building one yourself --------------------------------------------

  {
    setup: {
      reset: true,
      spawns: [12, 12],
      cells: {
        '0,0,0': 'p0',
        '-1,1,0': 'p0',
        '0,-2,2': 'k0',
        '3,0,-3': 'p1',
        // A rhombus, off to one side: four pieces, no elbow, no bow.
        '-3,3,0': 'p0',
        '-2,3,-1': 'p0',
        '-3,4,-1': 'p0',
        '-2,4,-2': 'p0',
        [RIVAL_KING_PARKED]: 'k1',
      },
    },
  },
  {
    say:
      'A bow needs the elbow shape specifically, and the cell directly behind has to be empty — that is where the string is drawn back to. The four pawns bunched together on the left are a rhombus, not a bow: no 120° elbow, no shot.',
  },
  { say: 'This one is half built. Place a pawn from your reserve to finish the elbow.' },
  {
    await: { type: 'spawnPiece', to: '0,-1,1' },
    hint: ['0,-1,1'],
    nudge: 'Your king can place a pawn on the ringed cell, which completes the elbow.',
  },
  { say: 'Now fire it.' },
  {
    await: { type: 'movePiece', from: '0,0,0', to: '3,0,-3', glide: true },
    hint: ['0,0,0', '3,0,-3'],
    nudge: 'Pick up the pawn at the elbow and send it down the ray.',
  },
  { wait: 500 },

  // --- kings are not special --------------------------------------------

  {
    setup: {
      reset: true,
      spawns: [12, 12],
      cells: { '0,0,0': 'p0', '1,0,-1': 'k1', [LEARNER_KING_PARKED]: 'k0' },
    },
  },
  {
    say:
      'Kings are not protected from capture the way they are in chess. Any piece can take one. Take that king with your pawn.',
  },
  {
    await: { type: 'movePiece', to: '1,0,-1' },
    hint: ['1,0,-1'],
    nudge: 'Move your pawn onto the red king.',
  },
  { wait: 500 },

  // --- check ------------------------------------------------------------

  {
    setup: {
      reset: true,
      spawns: [12, 12],
      cells: { '0,0,0': 'k0', '1,0,-1': 'p1', [RIVAL_KING_PARKED]: 'k1' },
    },
  },
  {
    say:
      'Some formations give you two or three kings, and while you have more than one, losing one is a risk you are allowed to take. Your last king is different: it must end your turn safe.',
  },
  {
    say:
      'Yours is your last, and it is attacked — the ring around it says so. Get out of it.',
  },
  {
    await: {},
    hint: ['0,0,0'],
    nudge: 'Move the king somewhere safe, or take the pawn attacking it.',
  },
  {
    say:
      'Whatever you tried, the board only offered you moves that resolved it. A move that leaves your last king capturable is not a legal move, so it is never on the board.',
  },
  { wait: 500 },

  // --- checkmate --------------------------------------------------------

  {
    setup: {
      reset: true,
      spawns: [12, 12],
      cells: {
        '0,0,0': 'k1',
        '0,1,-1': 'p1',
        '-2,0,2': 'p0',
        '-2,1,1': 'p0',
        '2,-1,-1': 'k0',
      },
    },
  },
  {
    say:
      'When a player has no legal move at all, that is checkmate, and the game is over. Red is one move away from it.',
  },
  {
    await: { type: 'movePiece', from: '-2,0,2', to: '-1,0,1' },
    hint: ['-2,0,2', '-1,0,1'],
    nudge: 'Advance the ringed pawn one cell towards the red king.',
  },
  {
    say:
      'That is mate. The red king is attacked, nowhere it can go is safe, and the attacker is right beside it so nothing can be interposed.',
  },
  { wait: 500 },

  // --- the clock and the rest -------------------------------------------

  {
    say:
      'Two last things. Each side has a clock, and running out loses — but it does not start until both players have moved, so a game nobody turns up for is abandoned rather than won.',
  },
  {
    say:
      'And if neither of you can make progress, passing twice in a row is a draw, as is the same position occurring three times.',
  },
  { say: "That's everything. Go and play." },
];
